import * as React from 'react';
import { useState, useEffect } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { getAllSessions } from '../api/sessions';
import dayjs from 'dayjs';

export default function ChartTipsByBarber({ selectedDate, barberName }) {
  const [loading, setLoading] = useState(true);
  const [barberData14Days, setBarberData14Days] = useState([]); // 14-day data
  const [barberDataSelectedDay, setBarberDataSelectedDay] = useState([]); // Selected day data
  const [totalTips14Days, setTotalTips14Days] = useState(0); // Total tips for 14 days
  const [totalTipsSelectedDay, setTotalTipsSelectedDay] = useState(0); // Total tips for selected day
  const [barberTips14Days, setBarberTips14Days] = useState(0); // Selected barber's tips (14 days)
  const [barberTipsSelectedDay, setBarberTipsSelectedDay] = useState(0); // Selected barber's tips (selected day)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAllSessions();

        // Define date range (last 14 days from selectedDate)
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 13);
        startDate.setHours(0, 0, 0, 0);

        // Define selected day range
        const selectedDayStart = new Date(selectedDate);
        selectedDayStart.setHours(0, 0, 0, 0);
        const selectedDayEnd = new Date(selectedDate);
        selectedDayEnd.setHours(23, 59, 59, 999);

        // Filter sessions for 14-day range
        const filteredData14Days = data.filter(session => {
          const sessionDate = new Date(session.date);
          return sessionDate >= startDate && sessionDate <= endDate;
        });

        // Filter sessions for selected day
        const filteredDataSelectedDay = data.filter(session => {
          const sessionDate = new Date(session.date);
          return sessionDate >= selectedDayStart && sessionDate <= selectedDayEnd;
        });

        console.log('Filtered 14-Day Sessions:', filteredData14Days);
        console.log('Filtered Selected Day Sessions:', filteredDataSelectedDay);

        // Calculate tips per barber for 14 days
        const barberTipsData14Days = {};
        filteredData14Days.forEach(session => {
          if (session.barber_name && session.tip) {
            barberTipsData14Days[session.barber_name] = (barberTipsData14Days[session.barber_name] || 0) + session.tip;
          }
        });

        // Calculate tips per barber for selected day
        const barberTipsDataSelectedDay = {};
        filteredDataSelectedDay.forEach(session => {
          if (session.barber_name && session.tip) {
            barberTipsDataSelectedDay[session.barber_name] = (barberTipsDataSelectedDay[session.barber_name] || 0) + session.tip;
          }
        });

        console.log('14-Day Barber Tips Data:', barberTipsData14Days);
        console.log('Selected Day Barber Tips Data:', barberTipsDataSelectedDay);

        // Calculate total tips
        const total14Days = Object.values(barberTipsData14Days).reduce((acc, tip) => acc + tip, 0);
        const totalSelectedDay = Object.values(barberTipsDataSelectedDay).reduce((acc, tip) => acc + tip, 0);
        setTotalTips14Days(total14Days);
        setTotalTipsSelectedDay(totalSelectedDay);

        // Set selected barber's tips
        setBarberTips14Days(barberTipsData14Days[barberName] || 0);
        setBarberTipsSelectedDay(barberTipsDataSelectedDay[barberName] || 0);

        // Convert to array format for PieChart (14 days)
        const barberArray14Days = Object.entries(barberTipsData14Days).map(([name, value]) => ({
          label: name,
          value,
        }));
        setBarberData14Days(barberArray14Days);

        // Convert to array format for selected day display
        const barberArraySelectedDay = Object.entries(barberTipsDataSelectedDay).map(([name, value]) => ({
          label: name,
          value,
        }));
        setBarberDataSelectedDay(barberArraySelectedDay);

      } catch (error) {
        console.error('Error fetching session data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, barberName]);

  const colors = ['hsl(220, 20%, 65%)', 'hsl(220, 20%, 42%)', 'hsl(220, 20%, 35%)', 'hsl(220, 20%, 25%)'];

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: '3px', flexGrow: 1, minWidth: 240 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Tips by Barber ( 14 dag )
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
            series={[{ data: barberData14Days, innerRadius: 50, outerRadius: 60 }]}
            height={200}
            width={200}
            slotProps={{ legend: { hidden: true } }}
          >
            <text x="50%" y="45%" textAnchor="middle" fontSize="18px" fontWeight="bold">
              {totalTips14Days.toFixed(2)}
            </text>
            <text x="50%" y="60%" textAnchor="middle" fontSize="12px" fill="gray">
              Total Tips
            </text>
          </PieChart>
        </Box>

        {barberData14Days.length > 0 ? (
          barberData14Days.map((barber, index) => (
            <Stack key={index} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
              <Stack sx={{ gap: 1, flexGrow: 1 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: '500' }}>
                    {barber.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ${barber.value.toFixed(2)} ({((barber.value / totalTips14Days) * 100).toFixed(1)}%)
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(barber.value / totalTips14Days) * 100}
                  sx={{ [`& .${linearProgressClasses.bar}`]: { backgroundColor: colors[index % colors.length] } }}
                />
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No tips data available for the last 14 days.
          </Typography>
        )}

        {/* All Barbers - Selected Day */}
        <Typography variant="body2" sx={{ fontWeight: '600', mt: 2 }}>
          {dayjs(selectedDate).format('MMM D, YYYY')}
        </Typography>
        {barberTipsSelectedDay > 0 ? (
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2, pb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: '500' }}>
              {barberName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              ${barberTipsSelectedDay.toFixed(2)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No tips data available for {barberName} on {dayjs(selectedDate).format('MMM D, YYYY')}.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}